import { navigateTo, simpleRoute } from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import { BarChart3 } from '../components/icon/solar-icons.js'
import { Spinner } from '../components/spinner/spinner.js'
import { MenuItem, View } from '../types.js'
import { loadBalancetimeAuthAndData, loadBalancetimeData } from '../utils/load-utils.js'
import type { BalanceTimeViewProps } from '../views/playtime/balancetime/balancetime.js'

type RangeType = 'week' | 'month' | 'quarter' | 'year'

// ── Model ────────────────────────────────────────────────────────────────────

class BalanceTimeModel {
    // Legacy redirect — balancetime now lives at /me/balancetime. Preserve the
    // Harvest OAuth ?code= callback query string.
    public route = simpleRoute<View>('/balancetime', {
        onEnter: () => navigateTo('/me/balancetime' + window.location.search),
        render: () => <Spinner />,
    })

    public menuItem: MenuItem = {
        text: t.playtime.balancetime.menuLabel,
        icon: BarChart3,
        url: this.route.toUrl(),
        matchingRoutes: [this.route],
    }

    public onEnter(view: View) {
        const bt = view.state.balancetime
        const hasOAuthCode = new URLSearchParams(window.location.search).has('code')

        // Skip if already loaded (idempotent), unless there's an OAuth code to process
        if (bt.authStatus?.isAuthenticated && bt.data && !hasOAuthCode) return

        void loadBalancetimeAuthAndData(view)
    }

    public getProps(view: View): BalanceTimeViewProps {
        const { state } = view
        const bt = state.balancetime
        const lang = state.lang

        return {
            lang,
            embedded: true,

            // Auth
            authStatus: bt.authStatus,

            // Data
            data: bt.data,
            isLoading: bt.isLoading,
            loadError: bt.loadError,

            // Filter state
            rangeType: bt.rangeType,
            selectedUser: bt.selectedUser,

            // Callbacks
            onRangeChange: (newRange) => {
                this.handleRangeChange(view, newRange)
            },
            onOffsetChange: (delta) => {
                this.handleOffsetChange(view, delta)
            },
            onOffsetSet: (newOffset) => {
                this.handleOffsetSet(view, newOffset)
            },
            onUserChange: (userId) => {
                this.handleUserChange(view, userId)
            },
        }
    }

    // ── Handlers ──────────────────────────────────────────────────────────

    private handleRangeChange(view: View, newRange: RangeType) {
        const bt = view.state.balancetime

        bt.rangeType = newRange
        bt.offset = 0

        void loadBalancetimeData(view, {
            range_type: newRange,
            offset: '0',
            ...(bt.selectedUser ? { selected_user: bt.selectedUser } : {}),
        })
    }

    private handleOffsetChange(view: View, delta: number) {
        const bt = view.state.balancetime
        const newOffset = bt.offset + delta
        bt.offset = newOffset
        void loadBalancetimeData(view, {
            offset: String(newOffset),
            ...(bt.selectedUser ? { selected_user: bt.selectedUser } : {}),
        })
    }

    private handleOffsetSet(view: View, newOffset: number) {
        const bt = view.state.balancetime
        bt.offset = newOffset
        void loadBalancetimeData(view, {
            offset: String(newOffset),
            ...(bt.selectedUser ? { selected_user: bt.selectedUser } : {}),
        })
    }

    private handleUserChange(view: View, userId: string) {
        const bt = view.state.balancetime
        bt.selectedUser = userId
        void loadBalancetimeData(view, { selected_user: userId })
    }
}

export const balancetimeModel = new BalanceTimeModel()
